import { DOCUMENT } from "@angular/common";
import { inject, Injectable } from "@angular/core";
import { Meta, Title } from "@angular/platform-browser";
import { TranslocoService } from "@jsverse/transloco";
import { environment } from "../../environments/environment";
import { ItemsLanguages } from "../api/packbo/models/items-languages";

export type SeoTranslation = {
    languages_code?: string | ItemsLanguages | null;
    title?: string | null;
    meta_description?: string | null;
    slug?: string | null;
};

export type SeoConfig = {
    title?: string;
    description?: string;
    image?: string;
    isPrivate?: boolean;
    noIndex?: boolean;
    noFollow?: boolean;
    translations?: SeoTranslation[];
};

@Injectable({
    providedIn: "root"
})
export class SeoService {
    private readonly baseUrl = environment.baseUrl;
    private readonly siteName = "PackBo";

    private readonly meta = inject(Meta);
    private readonly title = inject(Title);
    private readonly transloco = inject(TranslocoService);
    private readonly document = inject(DOCUMENT);

    public updateMetaTags(config: SeoConfig, path: string): void {
        const currentLang = this.transloco.getActiveLang();

        const translation = this.getCurrentTranslation(config.translations, currentLang);

        const titleText =
            translation?.title ||
            (config.title ? `${config.title} | ${this.siteName}` : `${this.siteName} – L'expert en emballage`);
        this.title.setTitle(titleText);

        if (translation?.meta_description) {
            this.meta.updateTag({ name: "description", content: translation.meta_description });
        }

        const canonicalUrl = `${this.baseUrl}${path}`;
        this.updateCanonicalUrl(canonicalUrl);

        this.updateHreflangTags(path, config.translations);

        this.meta.updateTag({ property: "og:title", content: titleText });
        this.meta.updateTag({ property: "og:description", content: translation?.meta_description || "" });
        this.meta.updateTag({ property: "og:url", content: canonicalUrl });
        this.meta.updateTag({ property: "og:locale", content: currentLang === "fr" ? "fr_CA" : "en_US" });
        this.meta.updateTag({ property: "og:site_name", content: this.siteName });
        if (config.image) {
            this.meta.updateTag({ property: "og:image", content: config.image });
        }

        const robotsContent = this.buildRobotsContent(config);
        this.meta.updateTag({ name: "robots", content: robotsContent });
    }

    private getCurrentTranslation(
        translations: SeoTranslation[] | undefined,
        currentLang: string
    ): SeoTranslation | null {
        if (!translations || translations.length === 0) {
            return null;
        }

        const currentTranslation = translations.find((t) => {
            if (!t.languages_code) {
                return false;
            }

            const langCode = typeof t.languages_code === "string" ? t.languages_code : t.languages_code.code;
            return langCode?.toLowerCase().includes(currentLang.toLowerCase());
        });

        const fallbackTranslation = translations.find((t) => {
            if (!t.languages_code) {
                return false;
            }

            const langCode = typeof t.languages_code === "string" ? t.languages_code : t.languages_code.code;
            return langCode?.toLowerCase().includes("fr");
        });

        return currentTranslation || fallbackTranslation || translations[0] || null;
    }

    private buildRobotsContent(config: SeoConfig): string {
        if (config.isPrivate) {
            return "noindex,nofollow";
        }

        const index = config.noIndex ? "noindex" : "index";
        const follow = config.noFollow ? "nofollow" : "follow";

        return `${index},${follow}`;
    }

    private updateCanonicalUrl(url: string): void {
        let canonicalElement = this.document.querySelector("link[rel='canonical']");
        if (!canonicalElement) {
            canonicalElement = this.document.createElement("link");
            canonicalElement.setAttribute("rel", "canonical");
            this.document.head.appendChild(canonicalElement);
        }
        canonicalElement.setAttribute("href", url);
    }

    private updateHreflangTags(path: string, translations?: SeoTranslation[]): void {
        const existingHreflangs = this.document.querySelectorAll("link[rel='alternate'][hreflang]");
        existingHreflangs.forEach((tag) => tag.remove());

        const languages = ["fr", "en"];

        if (translations && translations.length > 0) {
            const frTranslation = translations.find((t) => {
                const langCode = typeof t.languages_code === "string" ? t.languages_code : t.languages_code?.code;
                return langCode?.toLowerCase().includes("fr");
            });

            const enTranslation = translations.find((t) => {
                const langCode = typeof t.languages_code === "string" ? t.languages_code : t.languages_code?.code;
                return langCode?.toLowerCase().includes("en");
            });

            if (frTranslation?.slug) {
                const xDefaultUrl = `${this.baseUrl}/fr/${frTranslation.slug}`;
                this.createHreflangTag("x-default", xDefaultUrl);
            }

            if (frTranslation?.slug) {
                this.createHreflangTag("fr", `${this.baseUrl}/fr/${frTranslation.slug}`);
            }
            if (enTranslation?.slug) {
                this.createHreflangTag("en", `${this.baseUrl}/en/${enTranslation.slug}`);
            }
        } else {
            const pathWithoutLang = path.replace(/^\/(fr|en)/, "");
            const xDefaultUrl = `${this.baseUrl}/fr${pathWithoutLang}`;
            this.createHreflangTag("x-default", xDefaultUrl);
            languages.forEach((lang) => {
                const url = `${this.baseUrl}/${lang}${pathWithoutLang}`;
                this.createHreflangTag(lang, url);
            });
        }
    }

    private createHreflangTag(lang: string, url: string): void {
        const link = this.document.createElement("link");
        link.setAttribute("rel", "alternate");
        link.setAttribute("hreflang", lang);
        link.setAttribute("href", url);
        this.document.head.appendChild(link);
    }
}
